/**
 * @fileoverview Integration Management Service
 * 
 * Frontend service for managing CMMS integrations (enable/disable,
 * credentials, connection testing).
 */

import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE_PATH = `${VITE_CORE_API_URL}/core/integrations`;

export interface IntegrationInfo {
	id: number;
	name: string;
	description: string;
	authSchema: {
		type: string;
		fields: Array<{
			key: string;
			label: string;
			type: string;
			required: boolean;
			helpText?: string;
		}>;
	};
	isActive: boolean;
	isEnabled: boolean;
	hasCredentials: boolean;
	connectedAt: string | null;
	orgIntegrationId: number | null;
}

export interface ConnectionTestResult {
	connected: boolean;
	details?: Record<string, any>;
}

/**
 * Fetch all available integrations with their enabled status for the current org
 */
export async function getAvailableIntegrations(): Promise<IntegrationInfo[]> {
	const response = await fetch(BASE_PATH, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to fetch integrations");
	}

	const result = await response.json();
	return result.data?.integrations || [];
}

/**
 * Enable an integration for the current org
 */
export async function enableIntegration(integrationId: number): Promise<void> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/enable`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to enable integration");
	}
}

/**
 * Disable an integration for the current org
 */
export async function disableIntegration(integrationId: number): Promise<void> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/disable`, {
		method: "POST",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to disable integration");
	}
}

/**
 * Update credentials for an integration
 */
export async function updateIntegrationCredentials(
	integrationId: number,
	credentials: Record<string, string>,
): Promise<void> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/credentials`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify(credentials),
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to update credentials");
	}
}

/**
 * Test connection for an integration (uses stored credentials if none provided)
 */
export async function testIntegrationConnection(
	integrationId: number,
	credentials?: Record<string, string>,
): Promise<ConnectionTestResult> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/test-connection`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: credentials ? JSON.stringify(credentials) : "{}",
	});

	const result = await response.json();

	if (!result.success) {
		throw new Error(result.message || "Connection test failed");
	}

	return result.data;
}

/**
 * Get the auth schema for an integration
 */
export async function getIntegrationAuthSchema(integrationId: number): Promise<any> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/auth-schema`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to fetch auth schema");
	}

	const result = await response.json();
	return result.data?.authSchema;
}

// ---- Sync Configuration ----

export type SyncDirection = "disabled" | "push" | "pull" | "bidirectional";
export type ConflictResolution = "facilityai_wins" | "external_wins" | "newest_wins" | "manual";

export interface DataTypeSyncConfig {
	dataType: string;
	direction: SyncDirection;
	conflictResolution: ConflictResolution;
	isEnabled: boolean;
	lastSyncedAt: string | null;
}

export interface IntegrationSyncConfig {
	dataTypes: DataTypeSyncConfig[];
	dryRunMode: boolean;
	requireApproval: boolean;
	auditLogEnabled: boolean;
	batchSize: number;
	schedule: string;
}

export interface IntegrationCapability {
	dataType: string;
	label: string;
	description: string;
	supportedDirections: SyncDirection[];
	implemented: boolean;
}

/**
 * Fetch sync configuration for an integration
 */
export async function getSyncConfig(integrationId: number): Promise<{
	syncConfig: IntegrationSyncConfig;
	capabilities: IntegrationCapability[];
}> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/sync-config`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to fetch sync configuration");
	}

	const result = await response.json();
	return result.data;
}

/**
 * Update sync configuration for an integration
 */
export async function updateSyncConfig(
	integrationId: number,
	syncConfig: IntegrationSyncConfig,
): Promise<void> {
	const response = await fetch(`${BASE_PATH}/${integrationId}/sync-config`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${await auth?.currentUser?.getIdToken()}`,
		},
		body: JSON.stringify(syncConfig),
	});

	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(error.message || "Failed to update sync configuration");
	}
}
